import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, Avatar,
  Select, MenuItem, FormControl, InputLabel, TextField, InputAdornment,
  IconButton, Tooltip,
} from '@mui/material';
import {
  People as PeopleIcon,
  Search as SearchIcon,
  Nfc as NfcIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import corporateService, { CorporateEmployee, CorporateClient } from '../../services/corporate.service';

const EmployeesPage: React.FC = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<CorporateClient[]>([]);
  const [employees, setEmployees] = useState<(CorporateEmployee & { company_nome: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterCompany, setFilterCompany] = useState<number | ''>('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const clients = await corporateService.getAll();
        setCompanies(clients);

        // Carregar funcionários de todas as empresas (ou só da seleccionada)
        const targets = filterCompany
          ? clients.filter(c => c.id === filterCompany)
          : clients;

        const results = await Promise.all(
          targets.map(async c => {
            const emps = await corporateService.getEmployees(c.id);
            return emps.map(e => ({ ...e, company_nome: c.nome, company_id: c.id }));
          })
        );
        setEmployees(results.flat());
      } catch {
        setError('Erro ao carregar funcionários');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [filterCompany]);

  const filtered = employees.filter(e => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      e.nome.toLowerCase().includes(q) ||
      (e.email || '').toLowerCase().includes(q) ||
      (e.departamento || '').toLowerCase().includes(q) ||
      (e.cargo || '').toLowerCase().includes(q)
    );
  });

  const ativos = filtered.filter(e => e.ativo).length;
  const comBracelet = filtered.filter(e => e.nfc_uid).length;

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Avatar sx={{ bgcolor: '#6233A0' }}><PeopleIcon /></Avatar>
        <Box>
          <Typography variant="h5" fontWeight={700}>Funcionários</Typography>
          <Typography variant="body2" color="text.secondary">
            Todos os funcionários registados no Corporate NFC
          </Typography>
        </Box>
      </Box>

      {/* Stats */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        {[
          { label: 'Total',        value: filtered.length, color: '#6233A0' },
          { label: 'Activos',      value: ativos,          color: '#10B981' },
          { label: 'Com bracelet', value: comBracelet,     color: '#0D7F9F' },
          { label: 'Sem bracelet', value: filtered.length - comBracelet, color: '#F59E0B' },
        ].map(s => (
          <Paper key={s.label} sx={{ p: 2, minWidth: 120, flex: 1 }}>
            <Typography variant="h4" fontWeight={700} color={s.color}>{s.value}</Typography>
            <Typography variant="caption" color="text.secondary">{s.label}</Typography>
          </Paper>
        ))}
      </Box>

      {/* Filtros */}
      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        <TextField
          size="small"
          placeholder="Pesquisar por nome, email, departamento..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ flex: 2, minWidth: 220 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Empresa</InputLabel>
          <Select
            value={filterCompany}
            label="Empresa"
            onChange={e => setFilterCompany(e.target.value as number | '')}
          >
            <MenuItem value="">Todas as empresas</MenuItem>
            {companies.map(c => (
              <MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell><b>Nome</b></TableCell>
                <TableCell><b>Empresa</b></TableCell>
                <TableCell><b>Departamento</b></TableCell>
                <TableCell><b>Cargo</b></TableCell>
                <TableCell align="right"><b>Recompensa (ECV)</b></TableCell>
                <TableCell align="center"><b>Bracelet</b></TableCell>
                <TableCell align="right"><b>Saldo Bonus</b></TableCell>
                <TableCell align="center"><b>Estado</b></TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Nenhum funcionário encontrado
                  </TableCell>
                </TableRow>
              ) : filtered.map(emp => (
                <TableRow key={`${emp.company_nome}-${emp.id}`} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{emp.nome}</Typography>
                    {emp.email && (
                      <Typography variant="caption" color="text.secondary">{emp.email}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 140 }}>
                      {emp.company_nome}
                    </Typography>
                  </TableCell>
                  <TableCell>{emp.departamento || '—'}</TableCell>
                  <TableCell>{emp.cargo || '—'}</TableCell>
                  <TableCell align="right">
                    {Number(emp.reward_amount).toLocaleString('pt-CV')}
                  </TableCell>
                  <TableCell align="center">
                    {emp.nfc_uid ? (
                      <Tooltip title={emp.nfc_uid}>
                        <Chip icon={<NfcIcon />} label="Vinculado" size="small" color="success" />
                      </Tooltip>
                    ) : (
                      <Chip label="Sem bracelet" size="small" color="default" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {emp.saldo_bonus != null && Number(emp.saldo_bonus) > 0
                      ? `${Number(emp.saldo_bonus).toLocaleString('pt-CV')} ECV`
                      : '—'}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={emp.ativo ? 'Activo' : 'Inactivo'}
                      size="small"
                      color={emp.ativo ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Ver empresa">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/corporate/${(emp as any).company_id}`)}
                      >
                        <OpenIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default EmployeesPage;
